import { useEffect, useState, type MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import { ThemeProvider } from '@/lib/theme'
import { toLocale, useI18n } from '@/lib/i18n'
import Header from '@/components/layout/Header'
import Footer from '@/components/layout/Footer'
import { useDocumentMeta } from '@/lib/useDocumentMeta'
import { PAGES, splitLocale } from '@/lib/seo'
import registry from '@/content/archive.json'
import '@/styles/imprint.css'
import '@/styles/archive.css'

/**
 * /archive — things people can take part in (user tests, surveys) and the
 * ones that have closed. Ongoing items link to their static pages under
 * /archive/<slug>/. Hosting rewrites /archive/** (any path that is no longer
 * a static item) to this page, which then says what happened to that item.
 */

type Copy = { kind: string; summary: string; period: string; closedNote: string }
export type ArchiveItem = {
  slug: string
  name: string
  status: 'open' | 'closed'
  minutes: string
  images: string[]
  /** the study page's background colour, for the "Take part" transition */
  background: string
  de: Copy
  en: Copy
}
export const ARCHIVE = registry.items as ArchiveItem[]

const TEXT = {
  de: {
    title: 'Archiv',
    intro: 'Hier finden Sie, woran Sie gerade mitwirken können, etwa Nutzertests neuer Apps. Die Teilnahme ist freiwillig, und wir fragen nie nach Ihrem Namen oder Ihrer E-Mail-Adresse.',
    privacyA: 'Was mit Ihren Antworten passiert, steht in unserer ',
    privacyLink: 'Datenschutzerklärung',
    open: 'Laufend',
    noneOpen: 'Gerade läuft nichts.',
    closed: 'Abgeschlossen',
    takePart: 'Teilnehmen',
    minutes: (m: string) => `ca. ${m} Minuten`,
    ended: (n: string) => `${n} ist beendet.`,
    endedFallback: 'Vielen Dank an alle, die mitgemacht haben.',
    unknown: 'Diese Seite gibt es nicht oder nicht mehr.',
  },
  en: {
    title: 'Archive',
    intro: 'This is where you can take part in what we are working on, such as user tests of new apps. Taking part is voluntary, and we never ask for your name or email address.',
    privacyA: 'What happens to your answers is explained in our ',
    privacyLink: 'privacy policy',
    open: 'Ongoing',
    noneOpen: 'Nothing is running right now.',
    closed: 'Completed',
    takePart: 'Take part',
    minutes: (m: string) => `about ${m} minutes`,
    ended: (n: string) => `${n} has closed.`,
    endedFallback: 'Thank you to everyone who took part.',
    unknown: 'This page does not exist or is no longer available.',
  },
}

/**
 * "Take part": a circle in the study's own background colour grows from the
 * button until it fills the screen, then the study opens and fades in on that
 * colour (its style.css). Plain navigation for modifier clicks and reduced motion.
 */
function openStudy(e: MouseEvent<HTMLAnchorElement>, bg: string) {
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  e.preventDefault()
  const href = e.currentTarget.href
  const r = e.currentTarget.getBoundingClientRect()
  const x = r.left + r.width / 2, y = r.top + r.height / 2
  const radius = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y))
  const veil = document.createElement('div')
  veil.className = 'archive-veil'
  veil.style.background = bg
  veil.style.clipPath = `circle(0px at ${x}px ${y}px)`
  document.body.appendChild(veil)
  const done = () => window.location.assign(href)
  const anim = veil.animate(
    [{ clipPath: `circle(0px at ${x}px ${y}px)` }, { clipPath: `circle(${radius}px at ${x}px ${y}px)` }],
    { duration: 520, easing: 'cubic-bezier(0.65, 0, 0.35, 1)', fill: 'forwards' },
  )
  anim.onfinish = done
  setTimeout(done, 900) // in case the animation never reports back
}

/** /archive/<slug>/… → slug; read after hydration so the prerendered /archive HTML still matches. */
function requestedSlug(): string | null {
  const { path } = splitLocale(window.location.pathname)
  const m = path.match(/^\/archive\/([^/]+)/)
  return m ? decodeURIComponent(m[1]) : null
}

/** Warm the study's page on hover so the circle hands over to a page that is ready. */
const prefetched = new Set<string>()
function prefetch(href: string) {
  if (prefetched.has(href)) return
  prefetched.add(href)
  const link = document.createElement('link')
  link.rel = 'prefetch'
  link.href = href
  document.head.appendChild(link)
}

function Cover({ item }: { item: ArchiveItem }) {
  return (
    <div className="archive-cover" aria-hidden="true">
      {item.images.slice(0, 2).map((src) => (
        <img key={src} src={src} alt="" loading="lazy" width={96} height={96} />
      ))}
    </div>
  )
}

export default function ArchivePage() {
  const { lang, to } = useI18n()
  const locale = toLocale(lang)
  const t = TEXT[locale]
  useDocumentMeta({ ...PAGES['/archive'][locale], path: '/archive', locale })

  const [asked, setAsked] = useState<string | null>(null)
  useEffect(() => setAsked(requestedSlug()), [])
  // Back from a study (page restored from the back/forward cache): drop the circle.
  useEffect(() => {
    const clear = (e: PageTransitionEvent) => { if (e.persisted) document.querySelectorAll('.archive-veil').forEach((v) => v.remove()) }
    window.addEventListener('pageshow', clear)
    return () => window.removeEventListener('pageshow', clear)
  }, [])
  const askedItem = asked ? ARCHIVE.find((s) => s.slug === asked) : undefined

  const open = ARCHIVE.filter((s) => s.status === 'open')
  const closed = ARCHIVE.filter((s) => s.status === 'closed')

  return (
    <ThemeProvider>
      <Header active="home" onNavigate={() => {}} />

      <main className="imprint-page">
        <div className="container imprint-content">
          <h1 className="imprint-title">{t.title}</h1>

          {asked && (!askedItem || askedItem.status === 'closed') && (
            <div className="archive-notice" role="status">
              {askedItem ? (
                <>
                  <p>
                    <strong>{t.ended(askedItem.name)}</strong>
                  </p>
                  <p>{askedItem[locale].closedNote || t.endedFallback}</p>
                </>
              ) : (
                <p>
                  <strong>{t.unknown}</strong>
                </p>
              )}
            </div>
          )}

          <section className="imprint-section">
            <p>{t.intro}</p>
            <p>
              {t.privacyA}
              <Link to={to('/privacy')}>{t.privacyLink}</Link>.
            </p>
          </section>

          <section className="imprint-section">
            <h2>{t.open}</h2>
            {open.length ? (
              <ul className="archive-list" role="list">
                {open.map((s) => (
                  <li key={s.slug} className="archive-item">
                    <Cover item={s} />
                    <div>
                      <p className="archive-kind mono muted">{s[locale].kind}</p>
                      <h3>{s.name}</h3>
                      <p>{s[locale].summary}</p>
                      <p className="mono muted">
                        {t.minutes(s.minutes)} · {s[locale].period}
                      </p>
                      <a
                        className="btn btn--primary"
                        href={`/archive/${s.slug}/?lang=${locale}`}
                        onClick={(e) => openStudy(e, s.background)}
                        onPointerEnter={() => prefetch(`/archive/${s.slug}/`)}
                      >
                        {t.takePart}
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">{t.noneOpen}</p>
            )}
          </section>

          {closed.length > 0 && (
            <section className="imprint-section">
              <h2>{t.closed}</h2>
              <ul className="archive-list" role="list">
                {closed.map((s) => (
                  <li key={s.slug} className="archive-item">
                    <Cover item={s} />
                    <div>
                      <p className="archive-kind mono muted">{s[locale].kind}</p>
                      <h3>{s.name}</h3>
                      <p className="mono muted">{s[locale].period}</p>
                      <p>{s[locale].closedNote || t.endedFallback}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </ThemeProvider>
  )
}
